import {inject, Injectable} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {ActivatedRouteSnapshot, PRIMARY_OUTLET, RouterStateSnapshot, TitleStrategy} from '@angular/router';

/**
 * Markenname, der an jeden Seitentitel angehängt wird.
 * Schreibweise ist bewusst „Nahaus“ (nie „naHaus“).
 */
export const NAHAUS_BRAND = 'Nahaus';

/**
 * Trenner zwischen Seitentitel und Marke: „Immobilien | Nahaus“.
 */
export const NAHAUS_TITLE_SEPARATOR = ' | ';

/**
 * Trenner innerhalb des Seitentitels zwischen Seite und Kontext: „Nebenkosten · Böttgerstraße 16-18“.
 * Derselbe Mittelpunkt, den die statischen Routentitel nutzen („Einstellungen · Immobilie“).
 */
export const NAHAUS_TITLE_CONTEXT_SEPARATOR = ' · ';

/**
 * Titel, wenn im aktiven Routenbaum kein `title` gesetzt ist.
 * Wird bewusst gesetzt, damit nach einem Wechsel von einer betitelten auf eine unbetitelte Seite
 * kein veralteter Titel im Tab stehen bleibt.
 */
export const NAHAUS_DEFAULT_DOCUMENT_TITLE = `${NAHAUS_BRAND}${NAHAUS_TITLE_SEPARATOR}Immobilienverwaltung`;

/**
 * Route-`data`-Schlüssel: `data: {ownsDocumentTitle: true}` schaltet die Strategie für diesen Routenzweig ab.
 * Gedacht für die öffentlichen Marketing-Seiten (Anmelden, Registrieren, Referral), die ihren
 * Dokumenttitel samt og:/twitter:-Meta-Tags selbst über den SeoService setzen.
 */
export const OWNS_DOCUMENT_TITLE_DATA_KEY = 'ownsDocumentTitle';

/** Statischer Routentitel der Immobilien-Detailseite; wird durch die Adresse ersetzt, sobald sie aufgelöst ist. */
const PROPERTY_ENTITY_TITLE = 'Immobilie';

/** Statischer Routentitel der Einheiten-Detailseite; wird durch die Einheitenbezeichnung ersetzt, sobald sie aufgelöst ist. */
const UNIT_ENTITY_TITLE = 'Einheit';

/**
 * Kontext, der aus den per `resolve` aufgelösten Routendaten gewonnen wird
 * (Schlüssel `property`, `unit`, `property_and_unit` – so heißen sie in den Routen des Immobilien-Moduls).
 */
export interface RouteTitleContext {
    /** Adresse der Immobilie, z. B. „Böttgerstraße 16-18“ */
    property?: string;
    /** Bezeichnung der Einheit, z. B. „W3 Dachgeschoss“ */
    unit?: string;
}

/**
 * Baut aus dem aufgelösten Routentitel den vollständigen Dokumenttitel.
 *
 * - „Immobilien“ → „Immobilien | Nahaus“
 * - leer/undefined → Standardtitel
 */
export function buildDocumentTitle(pageTitle: string | null | undefined): string {
    const trimmed = (pageTitle ?? '').trim();
    if (!trimmed) {
        return NAHAUS_DEFAULT_DOCUMENT_TITLE;
    }
    return `${trimmed}${NAHAUS_TITLE_SEPARATOR}${NAHAUS_BRAND}`;
}

/**
 * Läuft den aktiven Primär-Routenpfad von der Wurzel bis zum Blatt ab und prüft,
 * ob irgendeine Route `data.ownsDocumentTitle === true` trägt.
 */
export function routeOwnsDocumentTitle(snapshot: RouterStateSnapshot): boolean {
    let route: ActivatedRouteSnapshot | undefined = snapshot.root;
    while (route) {
        if (route.data?.[OWNS_DOCUMENT_TITLE_DATA_KEY] === true) {
            return true;
        }
        route = route.children.find(child => child.outlet === PRIMARY_OUTLET);
    }
    return false;
}

/**
 * Kurzadresse einer Immobilie für den Tab-Titel: „Straße Hausnummer“ (ohne PLZ/Ort, damit der Tab lesbar bleibt).
 * Liefert undefined, wenn kein Straßenname und keine Hausnummer vorliegen.
 */
export function propertyTitleLabel(property: unknown): string | undefined {
    const address = (property as {address?: {streetName?: unknown; streetNumber?: unknown}} | null | undefined)?.address;
    const streetName = typeof address?.streetName === 'string' ? address.streetName.trim() : '';
    const streetNumber = typeof address?.streetNumber === 'string' || typeof address?.streetNumber === 'number'
        ? String(address.streetNumber).trim()
        : '';
    const label = `${streetName} ${streetNumber}`.trim();
    return label || undefined;
}

/**
 * Bezeichnung einer Einheit für den Tab-Titel: „W<w_id> <label>“, analog zur Kopfzeile der Einheitenseite.
 * Liefert undefined, wenn weder Nummer noch Bezeichnung vorliegen.
 */
export function unitTitleLabel(unit: unknown): string | undefined {
    const candidate = unit as {w_id?: unknown; label?: unknown} | null | undefined;
    const number = typeof candidate?.w_id === 'number' && Number.isFinite(candidate.w_id) ? `W${candidate.w_id}` : '';
    const label = typeof candidate?.label === 'string' ? candidate.label.trim() : '';
    const parts = [number, label].filter(Boolean);
    return parts.length ? parts.join(' ') : undefined;
}

/**
 * Sammelt Immobilie und Einheit aus den aufgelösten Daten des Primär-Routenpfads.
 * Der tiefste Treffer gewinnt; Routen ohne passende `resolve`-Daten liefern schlicht keinen Kontext.
 */
export function collectRouteTitleContext(snapshot: RouterStateSnapshot): RouteTitleContext {
    const context: RouteTitleContext = {};
    let route: ActivatedRouteSnapshot | undefined = snapshot.root;
    while (route) {
        const data = route.data ?? {};
        const combined = data['property_and_unit'] as {property?: unknown; unit?: unknown} | null | undefined;
        const property = propertyTitleLabel(data['property'] ?? combined?.property);
        const unit = unitTitleLabel(data['unit'] ?? combined?.unit);
        if (property) {
            context.property = property;
        }
        if (unit) {
            context.unit = unit;
        }
        route = route.children.find(child => child.outlet === PRIMARY_OUTLET);
    }
    return context;
}

/**
 * Entfernt den generischen Entitätsnamen aus dem Seitentitel, wenn der konkrete Kontext bekannt ist:
 * „Immobilie“ → „“, „Einstellungen · Immobilie“ → „Einstellungen“.
 */
function stripEntityTitle(pageTitle: string, entity: string): string {
    if (pageTitle === entity) {
        return '';
    }
    const suffix = `${NAHAUS_TITLE_CONTEXT_SEPARATOR}${entity}`;
    return pageTitle.endsWith(suffix) ? pageTitle.slice(0, -suffix.length) : pageTitle;
}

/**
 * Verbindet Seitentitel und Kontext: „Nebenkosten · Böttgerstraße 16-18“, „Mieter · W3 Dachgeschoss · Böttgerstraße 16-18“.
 * Ist der Seitentitel nur der Entitätsname („Immobilie“, „Einheit“), ersetzt der Kontext ihn vollständig.
 * Ohne Kontext bleibt der Seitentitel unverändert.
 */
export function composePageTitle(pageTitle: string | null | undefined, context: RouteTitleContext): string | undefined {
    let page = (pageTitle ?? '').trim();
    if (context.property) {
        page = stripEntityTitle(page, PROPERTY_ENTITY_TITLE);
    }
    if (context.unit) {
        page = stripEntityTitle(page, UNIT_ENTITY_TITLE);
    }
    const parts = [page, context.unit, context.property].filter(Boolean);
    return parts.length ? parts.join(NAHAUS_TITLE_CONTEXT_SEPARATOR) : undefined;
}

/**
 * Setzt den Browser-Tab-Titel bei jeder erfolgreichen Navigation anhand des tiefsten
 * `title` im Primär-Routenpfad (Angular-Standard `buildTitle`), ergänzt um Immobilie/Einheit
 * aus den aufgelösten Routendaten, plus Marken-Suffix.
 *
 * Registriert in `app.config.ts` als `{provide: TitleStrategy, useClass: NahausTitleStrategy}`.
 */
@Injectable({providedIn: 'root'})
export class NahausTitleStrategy extends TitleStrategy {

    private readonly title = inject(Title);

    override updateTitle(snapshot: RouterStateSnapshot): void {
        if (routeOwnsDocumentTitle(snapshot)) {
            return;
        }
        const pageTitle = composePageTitle(this.buildTitle(snapshot), collectRouteTitleContext(snapshot));
        this.title.setTitle(buildDocumentTitle(pageTitle));
    }
}
